import type { Appointment } from '../../api/timetable'
import { whatItIsAbout } from '../../api/timetable'
import { Button } from '../../ui/Control'
import { readableDay, readableTime } from './week'

/**
 * What a single click on an appointment opens: a card that says what it is.
 *
 * A single click is a question — *what is that on Tuesday morning?* — and it is answered where it
 * was asked, without leaving the week. Opening a whole page for it would mean losing the grid and
 * finding the way back, for one line of information.
 *
 * **The page is a double click** — and the card carries the control that opens it, so the gesture
 * is the shortcut and never the only way.
 */
export interface PeekProps {
  readonly appointment: Appointment
  /** Where the click landed, so the card stands beside it rather than over it. */
  readonly at: { readonly x: number; readonly y: number }
  readonly onOpen: () => void
  readonly onRemove: () => void
  readonly onClose: () => void
}

export function Peek({ appointment, at, onOpen, onRemove, onClose }: PeekProps) {
  const about = whatItIsAbout(appointment)
  const from = new Date(appointment.startsAt)
  const until = new Date(appointment.endsAt)

  return (
    <>
      <button type="button" className="quick__away" aria-label="Close" onClick={onClose} />
      <div
        className="quick peek"
        role="dialog"
        aria-label={appointment.title}
        style={placed(at)}
        onKeyDown={(event) => {
          if (event.key === 'Escape') {
            event.preventDefault()
            onClose()
          }
        }}
      >
        <h3 className="peek__title">{appointment.title}</h3>

        <p className="quick__when">
          {appointment.allDay
            ? `${readableDay(dayOf(from))} — all day`
            : `${readableDay(dayOf(from))}, ${readableTime(from, appointment.zone)}–${readableTime(until, appointment.zone)}`}
        </p>

        <dl className="peek__facts">
          {about !== '' && (
            <>
              <dt>Subject</dt>
              <dd>{about}</dd>
            </>
          )}
          {appointment.room !== '' && (
            <>
              <dt>Room</dt>
              <dd>{appointment.room}</dd>
            </>
          )}
          {appointment.teacher !== '' && (
            <>
              <dt>Teacher</dt>
              <dd>{appointment.teacher}</dd>
            </>
          )}
          {appointment.note !== '' && (
            <>
              <dt>Note</dt>
              <dd>{appointment.note}</dd>
            </>
          )}
          {appointment.origin.kind !== 'by-hand' && (
            <>
              <dt>From</dt>
              <dd>{appointment.origin.source}</dd>
            </>
          )}
        </dl>

        {appointment.cancelled && <p className="peek__mark">Cancelled</p>}
        {appointment.remark !== '' && (
          <p className="peek__mark">
            {appointment.origin.source === '' ? '' : `${appointment.origin.source}: `}
            {appointment.remark}
          </p>
        )}
        {appointment.goneFromSourceAt !== null && (
          <p className="peek__mark">No longer offered by {appointment.origin.source}</p>
        )}

        <div className="controls controls--tight">
          <Button tone="primary" onClick={onOpen}>
            Open
          </Button>
          <Button tone="bare" className="control--danger" onClick={onRemove}>
            Delete
          </Button>
        </div>
      </div>
    </>
  )
}

/** The day a moment falls on, as `YYYY-MM-DD` in the reader's own zone. */
function dayOf(at: Date): string {
  const month = String(at.getMonth() + 1).padStart(2, '0')
  const day = String(at.getDate()).padStart(2, '0')
  return `${String(at.getFullYear())}-${month}-${day}`
}

/** Beside the click, and never off the screen. */
function placed(at: { x: number; y: number }): React.CSSProperties {
  const width = 19
  const left = Math.max(8, Math.min(at.x + 12, window.innerWidth - width * 16 - 8))
  const top = Math.max(8, Math.min(at.y - 24, window.innerHeight - 280))
  return { left: `${String(left)}px`, top: `${String(top)}px`, width: `${String(width)}rem` }
}
